export type ComboOverUnderSide = 'O' | 'U';

export type ComboOverUnderSelectionInput = {
    name: string;
    templateId?: string;
    identifier?: string;
    selectionId: unknown;
};

export type ComboOverUnderColumn<TSelectionId = unknown> = {
    header: string;
    overSelectionId: TSelectionId | undefined;
    underSelectionId: TSelectionId | undefined;
};

const TRAILING_LINE = /\s*[+\-]?\d+(?:\.\d+)?\s*$/;

const RESULT_ORDER: Record<string, number> = {
    h: 0,
    '1': 0,
    '1x': 0,
    hd: 0,
    d: 1,
    x: 1,
    '12': 1,
    ha: 1,
    a: 2,
    '2': 2,
    x2: 2,
    ad: 2,
    draw: 1,
};

const stripTrailingLine = (name: string): string => name.replace(TRAILING_LINE, '').trim();

const resolveOuSide = (selection: ComboOverUnderSelectionInput): ComboOverUnderSide | null => {
    const tid = (selection.identifier ?? selection.templateId ?? '').toLowerCase().trim();

    if (tid === 'u' || tid === 'un' || tid === 'under' || tid.endsWith('-u') || tid.endsWith('_u')) {
        return 'U';
    }
    if (tid === 'o' || tid === 'ov' || tid === 'over' || tid.endsWith('-o') || tid.endsWith('_o')) {
        return 'O';
    }

    const name = selection.name.toLowerCase();
    if (/\band\s+under\b/.test(name) || /(^|\s)under(\s|$)/.test(name)) {
        return 'U';
    }
    if (/\band\s+over\b/.test(name) || /(^|\s)over(\s|$)/.test(name)) {
        return 'O';
    }

    return null;
};

const stripOuFromName = (nameWithoutLine: string): string => {
    return nameWithoutLine
        .replace(/\s+and\s+over\s*$/i, '')
        .replace(/\s+and\s+under\s*$/i, '')
        .replace(/\s+over\s*$/i, '')
        .replace(/\s+under\s*$/i, '')
        .trim();
};

const columnSortWeight = (header: string, templateId: string | undefined, firstIndex: number): number => {
    const tid = (templateId ?? '').toLowerCase();
    const fromTemplate = RESULT_ORDER[tid.split(/[-_/]/)[0] ?? ''];
    if (fromTemplate !== undefined) {
        return fromTemplate;
    }

    const fromHeader = RESULT_ORDER[header.toLowerCase()];
    if (fromHeader !== undefined) {
        return fromHeader;
    }

    return 100 + firstIndex;
};

export const buildComboOverUnderColumns = <TSelectionId>(
    selections: Array<ComboOverUnderSelectionInput & { selectionId: TSelectionId }>
): Array<ComboOverUnderColumn<TSelectionId>> | null => {
    if (selections.length !== 6) {
        return null;
    }

    const byHeader = new Map<
        string,
        {
            header: string;
            overSelectionId: TSelectionId | undefined;
            underSelectionId: TSelectionId | undefined;
            templateId?: string;
            firstIndex: number;
        }
    >();

    for (let index = 0; index < selections.length; index += 1) {
        const selection = selections[index];
        if (selection === undefined) {
            return null;
        }

        const side = resolveOuSide(selection);
        if (side === null) {
            return null;
        }

        const header = stripOuFromName(stripTrailingLine(selection.name));
        if (header.length === 0) {
            return null;
        }

        const templateId = selection.identifier ?? selection.templateId;
        const existing = byHeader.get(header) ?? {
            header,
            overSelectionId: undefined,
            underSelectionId: undefined,
            templateId,
            firstIndex: index,
        };

        if (side === 'O') {
            existing.overSelectionId = selection.selectionId;
        } else {
            existing.underSelectionId = selection.selectionId;
        }
        if (existing.templateId === undefined) {
            existing.templateId = templateId;
        }

        byHeader.set(header, existing);
    }

    if (byHeader.size !== 3) {
        return null;
    }

    for (const column of byHeader.values()) {
        if (column.overSelectionId === undefined || column.underSelectionId === undefined) {
            return null;
        }
    }

    return Array.from(byHeader.values())
        .sort((a, b) => {
            const weightDiff =
                columnSortWeight(a.header, a.templateId, a.firstIndex) -
                columnSortWeight(b.header, b.templateId, b.firstIndex);
            if (weightDiff !== 0) {
                return weightDiff;
            }
            return a.firstIndex - b.firstIndex;
        })
        .map(({ header, overSelectionId, underSelectionId }) => ({
            header,
            overSelectionId,
            underSelectionId,
        }));
};

export const canStackComboOverUnder = (selections: Array<ComboOverUnderSelectionInput>): boolean => {
    return buildComboOverUnderColumns(selections) !== null;
};
